import { LinkedinTypes } from 'enums';
import _ from 'lodash';

export interface EmailAccount {
  _id: string; // unique id returned by backend (eaId)
  type: EmailAccountTypes; // 'smtp' | 'ms_o365' | 'gmail'
  username: string; // full e-mail address (used as alias)
  dailyLimit: number;
  dailySentCount: number;
  accessRevoked?: boolean; // flagged by backend if OAuth access got revoked
  dmarcError?: boolean; // set when backend detects DMARC/SPF/DKIM failures
}

export interface LinkedinAccount {
  _id: string;
  /** Set by the server when this LinkedIn login is already connected in another workspace. */
  connectedElsewhereAt?: string;
  createDate: string;
  profileId: string;
  // user data
  userEmail: string;
  linkedinEmail?: string;
  firstName?: string;
  lastName?: string;
  liProductType: LinkedinTypes;
  status: LinkedinAccountStatuses;
  statusMessage?: string;
  syncStage?: LinkedinAccountSyncStages;
  // NEW: multiple e-mail senders (phase-in alongside deprecated emailConfig)
  emailAccounts?: EmailAccount[];
  /**
   * @deprecated replaced by emailAccounts[] – kept read-only for transition period
   */
  emailConfig?: {
    type?: EmailAccountTypes;
    smtp?: EmailCredentials;
    imap?: EmailCredentials;
    oauth?: OAuthCredentials;
    gmailOauth?: OAuthCredentials;
    accessRevoked?: boolean;
  };
  countryName?: string;
  cityName?: string;
  contractName?: string;
  foundContractNames?: string[];
  takeDebugScreenshot?: boolean;
  thirdPartyTextPlaceholder?: string;
  thirdPartyTextAriaLabel?: string;
  thirdPartyTextType?: string;
  customInviteCharacterLimit?: number;
  signature?: string;
  // limits & schedule
  timeZone?: string;
  timezone?: string;
  workDays?: string[];
  workHours?: number[];
  connectionsLimit?: number;
  messagesLimit?: number;
  inmailDailyLimit?: number;
  emailsLimit?: number;
  coldCallsLimit?: number;
  cooldownAfterMessages?: number;
  cooldownAfterVisits?: number;
  visitDailyLimit?: number;
  usedToday?: {
    connections?: number;
    messages?: number;
    inmails?: number;
    visits?: number;
  };
  todayLimits?: {
    connections?: number | null;
    messages?: number | null;
    inmails?: number | null;
    visits?: number | null;
  };
  profilesCrawledToday?: number;
  profilesCrawledTodayLimit?: number | null;
  /** Temporary risk reduction after LinkedIn pushed back (only sent while active). */
  riskReduction?: {
    reason: string;
    since: string;
    until: string;
    factor: number;
  };
  /** Last proof that the Sales Navigator seat answers (set by app login + refresh). */
  seatAccess?: {
    product: 'sales_navigator' | 'recruiter';
    ok: boolean;
    checkedAt: string;
    httpStatus?: number;
    reason?: 'no_salesnav_jar' | 'no_recruiter_seat_cookie' | 'seat_not_answering' | 'bootstrap_error';
  };
  automationReadiness?: {
    state: 'ready' | 'deferred' | 'action_required' | 'needs_reconnect' | 'blocked';
    reason:
      | 'ready'
      | 'account_not_found'
      | 'login_in_progress'
      | 'connectivity_backoff'
      | 'login_timeout'
      | 'two_factor_required'
      | 'captcha_required'
      | 'wrong_credentials'
      | 'third_party_challenge'
      | 'contract_selection_required'
      | 'onboarding_incomplete'
      | 'fair_use_pause'
      | 'two_factor_not_enabled'
      | 'account_suspended'
      | 'logged_out'
      | 'login_error';
    message: string;
    failureCode?: string;
    retryAt?: string | null;
    delayMs?: number;
    shouldAutoRetry?: boolean;
    recommendedAction?:
      | 'wait'
      | 'complete_2fa'
      | 'solve_captcha'
      | 'update_credentials'
      | 'resolve_third_party_challenge'
      | 'select_contract'
      | 'complete_onboarding'
      | 'enable_two_factor'
      | 'reconnect_account'
      | 'contact_support';
    userAction?: string;
    session?: {
      present: boolean;
      status: string;
      hasLiAt: boolean;
      hasSessionCookie: boolean;
      validationErrorCount: number;
      expiresAt?: string | null;
    };
  };
}

export interface EmailCredentials {
  server: string;
  port: string;
  encryption: Encryptions;
  username: string;
  password: string;
}

export interface OAuthCredentials {
  email: string;
  code: string;
}

export interface CountryCode {
  name: string;
  code: string;
  cities: { prefix: string; options: { name: string; code: string }[] };
}

export enum Encryptions {
  NONE = 'none',
  SSL = 'ssl',
  TLS = 'tls',
  STARTTLS = 'starttls',
}

export const smtpEncryptionOptions = _.map(Encryptions, (value, key) => ({
  label: key,
  value: value,
}));

export const imapEncryptionOptions = _.chain(Encryptions)
  .omit(['STARTTLS'])
  .map((value, key) => ({
    label: key,
    value: value,
  }))
  .value();

export enum EmailAccountTypes {
  OTHER = 'other', // legacy mapping – treat as SMTP
  SMTP = 'smtp',
  MS_O365 = 'ms_o365',
  GMAIL = 'gmail',
}

export enum EmailCredentialErrors {
  SMTP = 'smtp',
  IMAP = 'imap',
  CREDENTIALS = 'credentials',
}

export enum LinkedinAccountStatuses {
  SYNCHRONIZED = 'synchronized',
  NOT_SYNCHRONIZED = 'not-synchronized',
  TWO_FA = '2fa',
  WRONG_PIN = 'wrong-pin',
  PENDING = 'pending',
  WRONG_CREDENTIALS = 'wrong-credentials',
  RECRUITER_PARALLEL_LOGIN = 'recruiter-parallel-login',
  TIMED_OUT_CAPTCHA = 'captcha-timed-out',
  TWO_FA_TIMED_OUT = '2fa-timed-out',
  CAPTCHA = 'captcha',
  ONBOARDING_INCOMPLETE = 'onboarding-incomplete',
  CHOOSE_CONTRACT = 'choose-contract',
  FAIR_USE_PAUSE = 'fair-use-pause',
  THIRD_PARTY = 'third-party',
  THIRD_PARTY_TEXT = 'third-party-text',
  TWO_FA_NOT_ENABLED = '2fa-not-enabled',
  /**
   * LinkedIn requires human intervention — captcha, 2FA timeout, repeated
   * login failures, or security checkpoint. All automation halted. Campaigns
   * that were running are paused and will auto-resume after manual re-login.
   */
  NEEDS_MANUAL_LOGIN = 'needs_manual_login',
}

/**
 * Es gibt zwei Statusvokabulare, und sie sind auseinandergelaufen.
 *
 * Das Backend schreibt `active`, `pending_2fa`, `wrong_credentials`, `captcha_required`.
 * Diese Aufzählung stammt aus der älteren Sprache: `synchronized`, `2fa`,
 * `wrong-credentials`, `captcha`. Die **Admin**-Route übersetzt zwischen beiden
 * (`statusToLegacy`, mit dem Kommentar „the frontend polls account endpoints and switches
 * on LinkedinAccountStatuses values"). Die **externe** Route tut das nicht — sie liefert
 * die rohen Werte, so steht es auch in ihrem Schema.
 *
 * Der Synchronisierungs-Dialog benutzt die externe Route. Ein geglückter Login kam dort
 * also als `active` an, traf im `switch` auf keinen Fall und landete im Standardzweig:
 * `activeStepIndex: -1` und `AppSyncErrors.UNKNOWN` — der Kunde sah nach einer
 * **erfolgreichen** Anmeldung einen Fehler und blieb im Fenster stehen. Belegt am
 * 12.08.2026 an Tobias Kluge: Backend `active` um 15:14:23, Oberfläche unverändert.
 * In den Logs steht „: active - ✅ Login successful" 31-mal und „: synchronized" **nie**.
 *
 * Statt die Übersetzung an einer zweiten Stelle nachzubauen (und beim nächsten neuen
 * Status wieder zu vergessen), wird hier normalisiert: was hereinkommt, wird auf die
 * Sprache gebracht, die der Dialog spricht — egal über welche Route es kam.
 */
const BACKEND_TO_LEGACY_STATUS: Record<string, LinkedinAccountStatuses> = {
  active: LinkedinAccountStatuses.SYNCHRONIZED,
  synchronized: LinkedinAccountStatuses.SYNCHRONIZED,
  onboarding: LinkedinAccountStatuses.PENDING,
  syncing: LinkedinAccountStatuses.PENDING,
  verification_pending: LinkedinAccountStatuses.PENDING,
  pending_2fa: LinkedinAccountStatuses.TWO_FA,
  'choose-contract': LinkedinAccountStatuses.CHOOSE_CONTRACT,
  captcha_required: LinkedinAccountStatuses.CAPTCHA,
  wrong_credentials: LinkedinAccountStatuses.WRONG_CREDENTIALS,
  error: LinkedinAccountStatuses.NOT_SYNCHRONIZED,
  suspended: LinkedinAccountStatuses.NOT_SYNCHRONIZED,
  logged_out: LinkedinAccountStatuses.NOT_SYNCHRONIZED,
};

/**
 * Bringt einen Kontostatus auf die Sprache dieser Aufzählung. Unbekanntes wird
 * unverändert durchgereicht — dann greift der Standardzweig des Aufrufers, und das ist
 * für einen wirklich unbekannten Zustand auch richtig.
 */
export function normalizeAccountStatus(status: string | undefined | null): string {
  if (!status) return '';
  return BACKEND_TO_LEGACY_STATUS[status] ?? status;
}

/** Recruiter/SN contract picker — including when the spinner only shows the status line. */
export function wantsContractSelection(status: string | undefined | null, statusMessage?: string): boolean {
  if (normalizeAccountStatus(status) === LinkedinAccountStatuses.CHOOSE_CONTRACT) return true;
  return /contract selection/i.test(String(statusMessage || ''));
}

export function contractNamesForPicker(account: {
  foundContractNames?: string[];
  contractName?: string;
} | null | undefined): string[] {
  const found = (account?.foundContractNames || []).map((n) => n.trim()).filter(Boolean);
  if (found.length) return found;
  const named = String(account?.contractName || '').trim();
  return named ? [named] : [];
}

export enum LinkedinAccountSyncStages {
  IN_QUEUE = 'inQueue',
  LOADING_PAGE = 'loadingPage',
  SIGNING_IN = 'signingIn',
  EXTRACTING_COOKIES = 'extractingCookies',
  EXTRACTING_QUERY_IDS = 'extractingQueryIDs',
}
